import { useEffect, useState } from 'react';
import { loadCreatorWorkspace } from '../creator/repository';
import type { CreatorCode, CreatorOverview, CreatorPayout, CreatorWorkspaceData } from '../creator/types';
import type { NavKey, Workspace } from '../types';

interface Props {
  workspace: Workspace;
  activePage: NavKey;
  writable: boolean;
}

// ---- formatting -------------------------------------------------------------
function usd(cents: number): string {
  const dollars = cents / 100;
  return dollars.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: Number.isInteger(dollars) ? 0 : 2,
    maximumFractionDigits: 2,
  });
}
function date(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
const referralLink = (code: string) => `https://mystakd.com/j/${code}`;

// ---- workspace --------------------------------------------------------------
export function CreatorPartnerWorkspace({ activePage }: Props) {
  const [data, setData] = useState<CreatorWorkspaceData | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  async function refresh() {
    setLoading(true);
    setError('');
    try {
      setData(await loadCreatorWorkspace());
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Your dashboard could not be loaded.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);
  if (loading) return <DashboardSkeleton />;
  if (error) return <DashboardError message={error} retry={refresh} />;
  if (!data) return null;

  switch (activePage) {
    case 'home':
      return <OverviewView data={data} />;
    case 'revenue-payouts':
      return <EarningsView data={data} />;
    case 'participants':
      return <ReachView data={data} />;
    case 'reports':
      return <StatementView data={data} />;
    case 'resources':
      return <ResourcesView />;
    case 'settings':
      return <SettingsView overview={data.overview} />;
    case 'help':
      return <HelpView />;
    default:
      return <OverviewView data={data} />;
  }
}

// ---- home / overview --------------------------------------------------------
function OverviewView({ data }: { data: CreatorWorkspaceData }) {
  const o = data.overview;
  const metrics: [string, string, string][] = [
    ['AVAILABLE', usd(o.availableCents), 'Past hold — next payout'],
    ['ON HOLD', usd(o.onHoldCents), 'Clears after the hold period'],
    ['LIFETIME EARNED', usd(o.lifetimeCents), 'Net of reversals'],
    ['PAID OUT', usd(o.paidCents), 'Across all payouts'],
    ['BUILDERS REFERRED', o.referredCount.toLocaleString(), 'Joined with your code'],
    ['PAYING BUILDERS', o.payingCount.toLocaleString(), 'On an active subscription'],
  ];
  return (
    <div className="page-grid">
      <section className="page-heading">
        <p className="eyebrow">CREATOR PARTNER</p>
        <h1>What we built together</h1>
        <p>Your community impact and everything STAKD owes you — aggregate and yours alone.</p>
      </section>

      <article className="panel hero-panel">
        <div>
          <p className="eyebrow">AVAILABLE TO PAY OUT</p>
          <strong className="hero-amount">{usd(o.availableCents)}</strong>
          <p>
            {o.payoutReady
              ? 'Above your payout minimum — this rolls into your next payout.'
              : `${usd(o.minPayoutCents)} minimum to trigger a payout. Keep building.`}
          </p>
        </div>
        <span className={o.payoutReady ? 'status paid' : 'status pending'}>
          {o.payoutReady ? 'Payout ready' : 'Building'}
        </span>
      </article>

      {data.onboarding?.hasApplication && data.onboarding.status !== 'active' ? (
        <article className="panel">
          <div className="section-heading"><div><p className="eyebrow">ONBOARDING</p><h2>Your path to Active</h2></div><span className={`status ${data.onboarding.status}`}>{data.onboarding.status?.replace(/_/g, ' ')}</span></div>
          <p className="muted">Current stage: {data.onboarding.stage?.replace(/_/g, ' ')}. Referral tools unlock only after every configured requirement is verified.</p>
          {data.onboarding.offer ? <div className="resource-row"><div><strong>Offer Summary · v{data.onboarding.offer.version}</strong><small>{data.onboarding.offer.planName} · issued {date(data.onboarding.offer.issuedAt)}</small><p>{data.onboarding.offer.summary}</p></div><span className={`status ${data.onboarding.offer.status}`}>{data.onboarding.offer.status}</span></div> : null}
          <div className="resource-list">{(data.onboarding.requirements ?? []).map((requirement) => <div className="resource-row" key={requirement.key}><div><strong>{requirement.label}</strong><small>{requirement.stage.replace(/_/g, ' ')}</small></div><span className={`status ${requirement.state}`}>{requirement.state}</span></div>)}</div>
        </article>
      ) : null}

      <section className="metric-grid">
        {metrics.map(([label, value, sub]) => (
          <Metric key={label} label={label} value={value} sub={sub} />
        ))}
      </section>

      <section className="dashboard-two">
        <article className="panel">
          <p className="eyebrow">YOUR STANDING</p>
          <h2>Classification &amp; plan</h2>
          <dl className="detail-list">
            <div>
              <dt>CLASSIFICATION</dt>
              <dd>{o.tier === 'founding' ? 'Founding Partner' : 'Creator Partner'}</dd>
            </div>
            <div>
              <dt>STATUS</dt>
              <dd>
                <span className={`status ${o.status}`}>{o.status}</span>
              </dd>
            </div>
            <div>
              <dt>COMMISSION PLAN</dt>
              <dd>{o.planName}</dd>
            </div>
            <div>
              <dt>PAYOUT MINIMUM</dt>
              <dd>{usd(o.minPayoutCents)}</dd>
            </div>
          </dl>
        </article>
        <article className="panel privacy-panel">
          <div className="privacy-five">✓</div>
          <div>
            <p className="eyebrow">COMMUNITY FIRST</p>
            <h2>Aggregate, always</h2>
            <p>{o.disclosure}</p>
            <small>Direct referrals only — no downstream, no MLM.</small>
          </div>
        </article>
      </section>
    </div>
  );
}

// ---- revenue & payouts ------------------------------------------------------
function EarningsView({ data }: { data: CreatorWorkspaceData }) {
  const o = data.overview;
  const buckets: [string, number, string][] = [
    ['Available', o.availableCents, 'Past hold, unpaid'],
    ['On hold', o.onHoldCents, 'Within the hold window'],
    ['Paid out', o.paidCents, 'Settled'],
    ['Reversed', -Math.abs(o.reversedCents), 'Refunds & chargebacks'],
  ];
  return (
    <div className="page-grid">
      <PageTitle eyebrow="REVENUE &amp; PAYOUTS" title="Your earnings" body={`How your commission moves from a referral to a payout under ${o.planName}. Each earning snapshots the plan and rule that produced it.`} />

      <section className="metric-grid">
        {buckets.map(([label, cents, sub]) => (
          <Metric key={label} label={label.toUpperCase()} value={usd(cents)} sub={sub} />
        ))}
      </section>

      <article className="panel">
        <div className="section-heading">
          <div>
            <p className="eyebrow">REFERRAL CODES</p>
            <h2>Your links &amp; reach</h2>
          </div>
          <span>{o.referredCount.toLocaleString()} referred · {o.payingCount.toLocaleString()} paying</span>
        </div>
        {data.codes.length === 0 ? (
          <p className="muted">No referral codes yet. STAKD mints your code when your partnership activates.</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Link</th>
                <th>State</th>
                <th className="num">Referred</th>
                <th className="num">Paying</th>
              </tr>
            </thead>
            <tbody>
              {data.codes.map((c) => (
                <CodeRow key={c.code} code={c} />
              ))}
            </tbody>
          </table>
        )}
      </article>

      <article className="panel">
        <div className="section-heading">
          <div>
            <p className="eyebrow">PAYOUT HISTORY</p>
            <h2>What STAKD has sent</h2>
          </div>
          <span>{usd(o.minPayoutCents)} plan minimum</span>
        </div>
        {data.payouts.length === 0 ? (
          <p className="muted">No payouts yet. Your available balance rolls into the next payout once it clears the minimum.</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Period</th>
                <th className="num">Amount</th>
                <th>Status</th>
                <th>Issued</th>
              </tr>
            </thead>
            <tbody>
              {data.payouts.map((p: CreatorPayout) => (
                <tr key={p.id}>
                  <td>{date(p.periodStart)} → {date(p.periodEnd)}</td>
                  <td className="num">{usd(p.amountCents)}</td>
                  <td><span className={`status ${p.status}`}>{p.status}</span></td>
                  <td>{date(p.issuedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </article>
    </div>
  );
}

function CodeRow({ code }: { code: CreatorCode }) {
  const [copied, setCopied] = useState(false);
  const link = referralLink(code.code);
  async function copy() {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable — the link is visible to copy manually */
    }
  }
  return (
    <tr>
      <td><strong>{code.code}</strong></td>
      <td>
        <button className="link-button" onClick={copy} title={link}>
          {copied ? 'Copied ✓' : 'Copy link'}
        </button>
      </td>
      <td><span className={`status ${code.active ? 'active' : 'retired'}`}>{code.active ? 'active' : 'inactive'}</span></td>
      <td className="num">{code.referredCount.toLocaleString()}</td>
      <td className="num">{code.payingCount.toLocaleString()}</td>
    </tr>
  );
}

// ---- reach (participants, privacy-safe) -------------------------------------
function ReachView({ data }: { data: CreatorWorkspaceData }) {
  const o = data.overview;
  const rate = o.referredCount ? Math.round((o.payingCount / o.referredCount) * 100) : 0;
  return (
    <div className="page-grid">
      <PageTitle eyebrow="YOUR REACH" title="Who you've brought in" body="Aggregate only. Individual builder identities and their money are never shown — that's the promise." />
      <section className="metric-grid">
        <Metric label="BUILDERS REFERRED" value={o.referredCount.toLocaleString()} sub="Joined with your code" />
        <Metric label="PAYING BUILDERS" value={o.payingCount.toLocaleString()} sub="On an active plan" />
        <Metric label="CONVERSION" value={`${rate}%`} sub="Referred → paying" />
        <Metric label="ACTIVE CODES" value={String(data.codes.filter((c) => c.active).length)} sub="Live referral links" />
      </section>
      <article className="panel privacy-panel">
        <div className="privacy-five">✓</div>
        <div>
          <p className="eyebrow">PRIVACY</p>
          <h2>Counts, never names</h2>
          <p>You see how many builders you've reached and how many subscribe — never who they are, what they earn, or what they spend.</p>
        </div>
      </article>
    </div>
  );
}

// ---- statement / reports ----------------------------------------------------
function StatementView({ data }: { data: CreatorWorkspaceData }) {
  const o = data.overview;
  const rows: [string, string][] = [
    ['Lifetime earned (net of reversals)', usd(o.lifetimeCents)],
    ['Paid out to date', usd(o.paidCents)],
    ['Available now', usd(o.availableCents)],
    ['On hold', usd(o.onHoldCents)],
    ['Reversed (refunds & chargebacks)', usd(-Math.abs(o.reversedCents))],
  ];
  return (
    <div className="page-grid">
      <PageTitle eyebrow="STATEMENT" title="Earnings statement" body="A plain-language summary of your commission. Downloadable statements land in a later phase." />
      <article className="panel">
        <table className="data-table">
          <tbody>
            {rows.map(([label, value]) => (
              <tr key={label}>
                <td>{label}</td>
                <td className="num"><strong>{value}</strong></td>
              </tr>
            ))}
          </tbody>
        </table>
      </article>
    </div>
  );
}

// ---- resources --------------------------------------------------------------
function ResourcesView() {
  return (
    <div className="page-grid">
      <PageTitle eyebrow="RESOURCES" title="How the partnership works" body="The essentials. Full agreements and brand assets land in the Agreement Center (a later phase)." />
      <section className="foundation-grid">
        <article className="panel">
          <h2>How you earn</h2>
          <p>Your approved Creator Earnings Plan defines eligible products, rates, and periods. Each earning snapshots the resolved plan rule, so a future plan change never rewrites your history.</p>
        </article>
        <article className="panel">
          <h2>Holds &amp; reversals</h2>
          <p>Earnings become available after the hold configured on your plan. Refunds and chargebacks are netted from future payouts — an ordinary cancellation is never clawed back.</p>
        </article>
        <article className="panel">
          <h2>Community first</h2>
          <p>Direct referrals only — no downstream, no recruiting, no MLM. Your dashboard leads with impact, not sales.</p>
        </article>
      </section>
    </div>
  );
}

// ---- settings ---------------------------------------------------------------
function SettingsView({ overview }: { overview: CreatorOverview }) {
  return (
    <div className="page-grid">
      <PageTitle eyebrow="SETTINGS" title="Your partner profile" body="Managed by STAKD. Contact support to update your handle, classification, or commission plan." />
      <article className="panel">
        <dl className="detail-list">
          <div><dt>DISPLAY NAME</dt><dd>{overview.displayName}</dd></div>
          <div><dt>HANDLE</dt><dd>{overview.handle}</dd></div>
          <div><dt>CLASSIFICATION</dt><dd>{overview.tier === 'founding' ? 'Founding Partner' : 'Creator Partner'}</dd></div>
          <div><dt>STATUS</dt><dd><span className={`status ${overview.status}`}>{overview.status}</span></dd></div>
          <div><dt>COMMISSION PLAN</dt><dd>{overview.planName}</dd></div>
          <div><dt>PAYOUT MINIMUM</dt><dd>{usd(overview.minPayoutCents)}</dd></div>
        </dl>
      </article>
    </div>
  );
}

// ---- help -------------------------------------------------------------------
function HelpView() {
  return (
    <div className="page-grid">
      <PageTitle eyebrow="HELP" title="Partner support" body="Questions about your earnings, codes, or payouts." />
      <section className="foundation-grid">
        <article className="panel"><h2>Payouts</h2><p>Available earnings roll into a payout once they clear the minimum configured on your plan. Contact STAKD to set your payout method.</p></article>
        <article className="panel"><h2>Your code</h2><p>Share your link — <code>mystakd.com/j/&lt;code&gt;</code> — anywhere. Builders who join through it are attributed to you, once, for life.</p></article>
        <article className="panel"><h2>Access</h2><p>Contact STAKD support to change your classification, plan, or partnership status.</p></article>
      </section>
    </div>
  );
}

// ---- shared -----------------------------------------------------------------
function Metric({ label, value, sub }: { label: string; value: string; sub: string }) {
  return <article className="metric-card"><span>{label}</span><strong>{value}</strong><small>{sub}</small></article>;
}
function PageTitle({ eyebrow, title, body }: { eyebrow: string; title: string; body: string }) {
  return <section className="page-heading"><p className="eyebrow">{eyebrow}</p><h1>{title}</h1><p>{body}</p></section>;
}
function DashboardSkeleton() {
  return <div className="page-grid"><div className="skeleton wide" /><div className="metric-grid">{Array.from({ length: 6 }, (_, index) => <div className="skeleton card" key={index} />)}</div><div className="skeleton table" /></div>;
}
function DashboardError({ message, retry }: { message: string; retry(): Promise<void> }) {
  return <div className="page-grid"><section className="panel empty-state"><p className="eyebrow">DATA UNAVAILABLE</p><h1>Your dashboard could not load</h1><div className="error">{message}</div><button className="primary-button compact" onClick={() => void retry()}>Try again</button></section></div>;
}
